import React, { useCallback, useMemo } from "react";
import { CCol, CFormInput, CFormSelect, CRow, CTooltip } from "@coreui/react";
import { CMultiSelect } from "@tradingdroid/coreui-pro";

import { useToastActions } from "@src/hooks/useToasts";
import { useParamOverrides } from "./ParamOverridesContext";
import { RangeInfo } from "./RangeInfo";
import type { CategoriesOverride, OverrideMode, ParamInfo, RangeOverride } from "./types";
import {
  getAvailableModes,
  getDefaultMode,
  getModeLabel,
  getOverrideMode,
  getTypeColor,
  getTypeIndicator,
  getTypeLabel,
  isBooleanSchema,
  isCategoricalSchema,
  isFixedOverride,
  isNumericSchema,
  isRangeOverride,
} from "./types";
import { getEffectiveCategories } from "./utils";

interface Props {
  param: ParamInfo;
}

export const ParamOverrideItem: React.FC<Props> = ({ param }) => {
  const { disabled, parameterSchema, overrides, currentValues, updateOverride } =
    useParamOverrides();
  const { showToast } = useToastActions();

  const defaultMode = getDefaultMode(param.schema, param.defaultValue);
  const currentMode = getOverrideMode(param.currentOverride, param.schema, param.defaultValue);
  const availableModes = getAvailableModes(param.schema);
  const hasOverride = param.currentOverride !== undefined;

  const handleModeChange = useCallback(
    (newMode: OverrideMode) => {
      // If selecting the default mode, clear the override
      if (newMode === defaultMode && hasOverride) {
        updateOverride(param.name, undefined);
        return;
      }

      // If already on the default mode with no override, create one
      // Use default value first since that's what backend uses when no override exists
      const { schema, defaultValue: paramDefault } = param;
      const defaultValue = paramDefault ?? schema?.default;

      if (newMode === "fixed") {
        // A parameter with no default seeds nothing: an empty box, and the payload leaves it out.
        // Picking a number for the user here -- the bottom of the range, say -- would assert a
        // value they never typed, which is the thing this whole path exists to stop.
        //
        // Still an override, holding null, and for the same reason clearing the box keeps one: it
        // is the only record that Fixed was chosen. Dropping it would re-derive the mode, so a
        // parameter that optimizes by default would bounce straight back to Range and the choice
        // could not be made at all.
        if (defaultValue == null) {
          updateOverride(param.name, { fixed: null });
          return;
        }
        updateOverride(param.name, { fixed: defaultValue });
      } else if (newMode === "range") {
        if (isNumericSchema(schema)) {
          updateOverride(param.name, {
            min: schema.low,
            max: schema.high,
            step: schema.step,
          });
        } else {
          const numValue = typeof defaultValue === "number" ? defaultValue : 0;
          updateOverride(param.name, {
            min: Math.max(0, numValue - 10),
            max: numValue + 10,
          });
        }
      } else if (newMode === "categories") {
        if (isBooleanSchema(schema)) {
          updateOverride(param.name, { categories: [...schema.categories] });
        } else if (isCategoricalSchema(schema)) {
          const effectiveCats = getEffectiveCategories(
            schema,
            currentValues,
            parameterSchema,
            overrides,
          );
          updateOverride(param.name, { categories: [...effectiveCats] });
        } else {
          if (typeof defaultValue === "boolean") {
            updateOverride(param.name, { categories: [defaultValue] });
          } else {
            updateOverride(param.name, { categories: [String(defaultValue)] });
          }
        }
      }
    },
    [param, defaultMode, hasOverride, currentValues, parameterSchema, overrides, updateOverride],
  );

  // Inline fixed value input - always show input, pre-filled with override or default
  const renderFixedInput = () => {
    const { schema, defaultValue } = param;
    const override = isFixedOverride(param.currentOverride) ? param.currentOverride : null;
    // Current value: override > default value > schema default
    // Default value takes precedence because that's what backend uses when no override is sent
    //
    // An override holding null stops the search rather than falling through it. Null here is the
    // user having emptied the box, which is not the same as having no override at all: reading
    // past it refills the box with the default they just cleared, and the next keystroke would
    // then edit that value instead of replacing it.
    const currentFixed =
      override?.fixed === null ? null : (override?.fixed ?? defaultValue ?? schema?.default);
    // Loose null check on purpose: a parameter with no default arrives as null, and String(null)
    // would hand the input the string "null". A number input sanitises that away and still looks
    // empty, so nothing is visibly wrong -- which is exactly why it is worth pinning. Formatting
    // an absent value as text and leaving the browser to discard it is one type change away from
    // being visible, and it hides the difference from anything that reads the DOM.

    // Numeric
    if (isNumericSchema(schema)) {
      return (
        <div className="d-flex align-items-center gap-1">
          <CFormInput
            type="number"
            size="sm"
            className="FixedInputNumber"
            value={currentFixed != null ? String(currentFixed) : ""}
            onChange={(e) => {
              // Clearing the box asserts no value. Number("") is 0, and 0 survives the isNaN
              // guard, so without this an emptied field asserts a zero -- which the exit config
              // then refuses, because zero is neither absent nor a usable threshold.
              //
              // A null fixed value, rather than dropping the override: the override is the only
              // record that the user chose Fixed at all. Removing it re-derives the mode from the
              // schema, so a parameter that optimizes by default would jump to Range inputs on the
              // keystroke that empties the box, mid-edit, taking the box being typed in with it.
              // Null keeps the row where the user put it, and the payload leaves the parameter out
              // exactly as an absent override would.
              if (e.target.value === "") {
                updateOverride(param.name, { fixed: null });
                return;
              }
              const numValue = Number(e.target.value);
              if (!isNaN(numValue)) updateOverride(param.name, { fixed: numValue });
            }}
            disabled={disabled}
            min={schema.low}
            max={schema.high}
            step={schema.step}
            aria-label={`Fixed value for ${param.name}`}
          />
          <RangeInfo schema={schema} />
        </div>
      );
    }

    // Boolean
    if (isBooleanSchema(schema)) {
      const boolValue = currentFixed ?? false;
      return (
        <CFormSelect
          size="sm"
          className="FixedInputBool"
          value={String(boolValue)}
          onChange={(e) => updateOverride(param.name, { fixed: e.target.value === "true" })}
          disabled={disabled}
          aria-label={`Fixed value for ${param.name}`}
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </CFormSelect>
      );
    }

    // Categorical
    if (isCategoricalSchema(schema)) {
      const effectiveCats = getEffectiveCategories(
        schema,
        currentValues,
        parameterSchema,
        overrides,
      );
      const catValue = currentFixed ?? effectiveCats[0];
      return (
        <CFormSelect
          size="sm"
          className="FixedInputCategorical"
          value={String(catValue)}
          onChange={(e) => updateOverride(param.name, { fixed: e.target.value })}
          disabled={disabled}
          aria-label={`Fixed value for ${param.name}`}
        >
          {effectiveCats.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </CFormSelect>
      );
    }

    // Fallback for params without schema - infer type from value
    if (typeof currentFixed === "number" || typeof defaultValue === "number") {
      return (
        <CFormInput
          type="number"
          size="sm"
          className="FixedInputNumber"
          value={currentFixed != null ? String(currentFixed) : ""}
          onChange={(e) => {
            // Clearing the box asserts no value instead of the zero Number("") produces -- the
            // same rule the schema-backed input above carries, held as a null fixed value for the
            // same reason: the override is what records the Fixed choice, so dropping it would
            // re-derive the mode and move the row out from under the user mid-edit.
            //
            // The absent-value form on the value line above is kept only so the two inputs read
            // alike: this fallback is entered only when the override or the default is already a
            // number, so the value it formats is never absent.
            if (e.target.value === "") {
              updateOverride(param.name, { fixed: null });
              return;
            }
            const numValue = Number(e.target.value);
            if (!isNaN(numValue)) updateOverride(param.name, { fixed: numValue });
          }}
          disabled={disabled}
          aria-label={`Fixed value for ${param.name}`}
        />
      );
    }

    if (typeof currentFixed === "boolean" || typeof defaultValue === "boolean") {
      return (
        <CFormSelect
          size="sm"
          className="FixedInputBool"
          value={String(currentFixed ?? false)}
          onChange={(e) => updateOverride(param.name, { fixed: e.target.value === "true" })}
          disabled={disabled}
          aria-label={`Fixed value for ${param.name}`}
        >
          <option value="true">true</option>
          <option value="false">false</option>
        </CFormSelect>
      );
    }

    // String fallback
    return (
      <CFormInput
        type="text"
        size="sm"
        className="FixedInputString"
        value={String(currentFixed ?? "")}
        // No empty-means-unset rule here, deliberately: an empty string is a value a user can mean,
        // unlike an empty number box, which has no value to stand for.
        onChange={(e) => updateOverride(param.name, { fixed: e.target.value })}
        disabled={disabled}
        aria-label={`Fixed value for ${param.name}`}
      />
    );
  };

  // Inline range inputs - always show inputs, pre-filled with override or schema defaults
  const renderRangeInputs = () => {
    if (!isNumericSchema(param.schema)) return null;

    // Store schema after type guard for use in nested functions
    const schema = param.schema;

    // Use override values if present, otherwise schema defaults
    const override = isRangeOverride(param.currentOverride) ? param.currentOverride : null;
    const currentMin = override?.min ?? schema.low;
    const currentMax = override?.max ?? schema.high;
    const currentStep = override?.step ?? schema.step;

    // Calculate preview of actual test values
    const getTestValuesPreview = () => {
      if (!currentStep || currentStep <= 0) return null;

      const values: number[] = [];
      const isFloat = schema.type === "FloatParameter";
      const precision = isFloat ? 2 : 0;

      for (let v = currentMin; v <= currentMax && values.length < 10; v += currentStep) {
        values.push(Number(v.toFixed(precision)));
      }

      // Check if max is exactly reachable
      const lastValue = values[values.length - 1];
      const maxReachable = Math.abs(lastValue - currentMax) < 0.0001;
      const hasMore = values.length >= 10;

      if (values.length <= 1) return null;

      const preview = hasMore ? `${values.slice(0, 5).join(", ")}...` : values.join(", ");
      const warning = !maxReachable && !hasMore ? ` (max ${currentMax} not reached)` : "";

      return { preview, warning };
    };

    const testPreview = getTestValuesPreview();

    const handleChange = (field: "min" | "max" | "step", value: string) => {
      // Build new override from current values (override or schema defaults)
      const baseOverride: RangeOverride = {
        min: override?.min ?? schema.low,
        max: override?.max ?? schema.high,
        ...(override?.step !== undefined ? { step: override.step } : {}),
      };

      if (field === "step") {
        if (value === "") {
          const { step: _, ...withoutStep } = baseOverride;
          updateOverride(param.name, withoutStep);
          return;
        }
        const numValue = Number(value);
        if (!isNaN(numValue)) updateOverride(param.name, { ...baseOverride, step: numValue });
        return;
      }

      if (value === "") return;
      const numValue = Number(value);
      if (!isNaN(numValue)) updateOverride(param.name, { ...baseOverride, [field]: numValue });
    };

    return (
      <div className="d-flex align-items-center gap-1 flex-wrap">
        <CFormInput
          type="number"
          size="sm"
          className="RangeInputMinMax"
          value={currentMin}
          onChange={(e) => handleChange("min", e.target.value)}
          disabled={disabled}
          placeholder="Min"
          aria-label={`Minimum value for ${param.name}`}
        />
        <span className="text-body-secondary">-</span>
        <CFormInput
          type="number"
          size="sm"
          className="RangeInputMinMax"
          value={currentMax}
          onChange={(e) => handleChange("max", e.target.value)}
          disabled={disabled}
          placeholder="Max"
          aria-label={`Maximum value for ${param.name}`}
        />
        <span className="text-body-secondary small">step:</span>
        <CFormInput
          type="number"
          size="sm"
          className="RangeInputStep"
          value={currentStep ?? ""}
          onChange={(e) => handleChange("step", e.target.value)}
          disabled={disabled}
          placeholder="auto"
          aria-label={`Step value for ${param.name}`}
        />
        {testPreview && (
          <CTooltip
            content={`Values: ${testPreview.preview}${testPreview.warning}`}
            placement="top"
          >
            <span className="d-inline-flex align-items-center justify-content-center InfoHint">
              {testPreview.warning ? "⚠️" : "ℹ️"}
            </span>
          </CTooltip>
        )}
      </div>
    );
  };

  // Category multi-select - always show, pre-filled with override or all categories
  const categorySelectData = useMemo(() => {
    const { schema } = param;

    // Get all available categories
    const allCategories: (string | boolean)[] = isBooleanSchema(schema)
      ? schema.categories
      : isCategoricalSchema(schema)
        ? getEffectiveCategories(schema, currentValues, parameterSchema, overrides)
        : [];

    if (allCategories.length === 0) return null;

    const override =
      param.currentOverride && "categories" in param.currentOverride
        ? (param.currentOverride as CategoriesOverride)
        : null;

    // Selected categories: from override, or all categories by default (optimize all)
    const selectedCategories = override?.categories ?? allCategories;
    const validSelected = selectedCategories.filter((cat) => allCategories.some((c) => c === cat));

    // Convert to CMultiSelect options format
    const options = allCategories.map((cat) => ({
      value: String(cat),
      label: String(cat),
      selected: validSelected.some((c) => c === cat),
    }));

    return { options, validSelected, allCategories };
  }, [param, currentValues, parameterSchema, overrides]);

  const handleCategoryChange = useCallback(
    (selected: { value: string | number }[]) => {
      if (!categorySelectData) return;

      // If trying to deselect all, keep the first available category and show warning
      if (selected.length === 0) {
        const firstCat = categorySelectData.allCategories[0];
        const keepValue = typeof firstCat === "boolean" ? firstCat : String(firstCat);
        updateOverride(param.name, { categories: [keepValue] });
        showToast({
          type: "warning",
          message: `At least one value for "${param.name}" must be selected`,
          timeout: 3000,
        });
        return;
      }

      // Convert back to original types - only convert to boolean for boolean schemas
      const isBoolSchema = isBooleanSchema(param.schema);
      const newCategories = selected.map((opt) => {
        const strVal = String(opt.value);
        if (isBoolSchema) {
          if (strVal === "true") return true;
          if (strVal === "false") return false;
        }
        return strVal;
      });

      updateOverride(param.name, { categories: newCategories });
    },
    [param.name, param.schema, categorySelectData, updateOverride, showToast],
  );

  const renderCategorySelect = () => {
    if (!categorySelectData) return null;

    const { options, validSelected } = categorySelectData;

    // Comma-separated list of selected values
    const selectedText = validSelected.map((cat) => String(cat)).join(", ");

    return (
      <div className="d-flex align-items-center gap-2">
        <span className="text-body-secondary text-truncate SelectedValuesHint" title={selectedText}>
          {selectedText}
        </span>
        <CMultiSelect
          size="sm"
          options={options}
          value={validSelected.map((cat) => String(cat))}
          onChange={handleCategoryChange}
          disabled={disabled}
          selectionType="counter"
          selectionTypeCounterText="selected"
          search={false}
          selectAll={false}
          cleaner={false}
          optionsStyle="checkbox"
          placeholder="Select..."
          aria-label={`Categories for ${param.name}`}
        />
      </div>
    );
  };

  // Render the appropriate value column based on current mode
  // Always show editable inputs, pre-filled with override or default values
  const renderValueColumn = () => {
    switch (currentMode) {
      case "range":
        return renderRangeInputs();
      case "fixed":
        return renderFixedInput();
      case "categories":
        return renderCategorySelect();
      default:
        return null;
    }
  };

  const hasDefaultValue = param.defaultValue !== undefined;
  const itemClassName = `ParamOverrideItem mb-2 py-1 px-2 border rounded${hasDefaultValue ? " fromDefault" : ""}`;

  const typeColor = getTypeColor(param.schema);
  const typeIndicator = getTypeIndicator(param.schema);
  const typeLabel = getTypeLabel(param.schema);

  return (
    <div className={itemClassName}>
      <CRow className="align-items-center g-2">
        {/* Column 1: Param name + type indicator */}
        <CCol xs="auto">
          <div className="d-flex align-items-center gap-1">
            <CTooltip content={`Type: ${typeLabel}`} placement="top">
              <span
                className="d-inline-flex align-items-center justify-content-center fw-bold TypeIndicator"
                style={{ color: typeColor }}
              >
                ({typeIndicator})
              </span>
            </CTooltip>
            {param.defaultValue !== undefined ? (
              <CTooltip content="Value from strategy defaults" placement="top">
                <strong className="small fst-italic">{param.name}</strong>
              </CTooltip>
            ) : (
              <strong className="small">{param.name}</strong>
            )}
          </div>
        </CCol>

        {/* Column 2: Mode selector */}
        <CCol xs="auto">
          <CFormSelect
            size="sm"
            className="ModeSelect"
            value={currentMode}
            onChange={(e) => handleModeChange(e.target.value as OverrideMode)}
            disabled={disabled}
            aria-label={`Override mode for ${param.name}`}
          >
            {availableModes.map((m) => (
              <option key={m} value={m}>
                {getModeLabel(m, defaultMode)}
              </option>
            ))}
          </CFormSelect>
        </CCol>

        {/* Column 3: Value/inputs */}
        <CCol className="d-flex justify-content-end">{renderValueColumn()}</CCol>
      </CRow>
    </div>
  );
};
